import createKDTree from 'static-kdtree';

const dumpPointsToAscii = (points, filename = 'cylinder_points.xyz') => {
  if (typeof document === 'undefined') return;
  const lines = points.map((p) => `${p[0]} ${p[1]} ${p[2]}`);
  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/**
 * Fit an infinite 3D cylinder to 3D points.
 * @param {number[][]} points Array of [x,y,z]
 * @param {object} [opts]
 * @param {number} [opts.k=20] kNN size for normal estimation
 * @param {number} [opts.ransacIters=800] unused (kept for compatibility)
 * @param {number} [opts.inlierThreshold=0.02] unused (kept for compatibility)
 * @param {number} [opts.minInlierRatio=0.5] unused (kept for compatibility)
 * @returns {{
 *   axisPoint:number[],
 *   axisDir:number[],
 *   radius:number,
 *   inliers:number[],
 *   normals:number[][]
 * }}
 */
export function fitInfiniteCylinder(points, opts = {}) {
  const { k = 120 } = opts;

  if (!Array.isArray(points) || points.length < 10) {
    throw new Error("fitInfiniteCylinder: need at least ~10 points");
  }

  //dumpPointsToAscii(points, `cylinder_points_${Date.now()}.xyz`);

  // --- 1) Build KD-tree (static points)
  const tree = createKDTree(points);

  try {
    // --- 2) Estimate normals by PCA on local neighborhood
    const normals = estimateNormalsPCA(points, tree, k);

    // --- 3) Axis direction = smallest-eigenvector of normals scatter matrix
    const axisDir = estimateAxisFromNormals(normals);

    // --- 4) Build orthonormal basis (u,v,d)
    const d = normalize(axisDir);
    const { u, v } = orthonormalBasisFromDir(d);

    // --- 5) Center points then project to 2D plane perpendicular to axis
    const centroid = [0, 0, 0];
    for (const p of points) {
      centroid[0] += p[0];
      centroid[1] += p[1];
      centroid[2] += p[2];
    }
    centroid[0] /= points.length;
    centroid[1] /= points.length;
    centroid[2] /= points.length;

    const pts2 = new Array(points.length);
    let tSum = 0;
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const rx = p[0] - centroid[0];
      const ry = p[1] - centroid[1];
      const rz = p[2] - centroid[2];
      pts2[i] = [
        rx * u[0] + ry * u[1] + rz * u[2],
        rx * v[0] + ry * v[1] + rz * v[2],
      ];
      tSum += rx * d[0] + ry * d[1] + rz * d[2];
    }

    // --- 6) Circle fit (least squares) on all points
    const allIdx = pts2.map((_, idx) => idx);
    const circle = fitCircleLeastSquares(pts2, allIdx);
    if (!circle) throw new Error("Least squares circle fit failed (degenerate data?)");

    // --- 7) Lift circle center back to 3D axis point
    // Choose axisPoint at mean t along axis to keep it "near" the cloud
    const meanT = tSum / Math.max(1, points.length);
    const axisPoint = add3(
      add3(add3(centroid, scale3(u, circle.cx)), scale3(v, circle.cy)),
      scale3(d, meanT)
    );

    return {
      axisPoint,
      axisDir: d,
      radius: circle.r,
      inliers: allIdx,
      normals,
    };
  } finally {
    tree.dispose(); // recommended by static-kdtree docs :contentReference[oaicite:1]{index=1}
  }
}

/* ------------------------- Normal estimation (PCA) ------------------------- */

function estimateNormalsPCA(points, tree, k) {
  const n = points.length;
  const normals = new Array(n);

  const kk = Math.max(6, Math.min(k, n - 1)); // PCA needs at least a few neighbors

  for (let i = 0; i < n; i++) {
    const p = points[i];
    // tree.knn returns indices of k closest points :contentReference[oaicite:2]{index=2}
    const idxs = tree.knn(p, kk + 1); // includes itself usually
    const neigh = [];
    for (const idx of idxs) {
      if (idx !== i) neigh.push(points[idx]);
    }
    if (neigh.length < 3) {
      normals[i] = [0, 0, 1];
      continue;
    }

    // centroid
    const c = [0, 0, 0];
    for (const q of neigh) {
      c[0] += q[0]; c[1] += q[1]; c[2] += q[2];
    }
    c[0] /= neigh.length; c[1] /= neigh.length; c[2] /= neigh.length;

    // covariance of centered neighbors
    const A = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0],
    ];
    for (const q of neigh) {
      const x = q[0] - c[0];
      const y = q[1] - c[1];
      const z = q[2] - c[2];
      A[0][0] += x * x; A[0][1] += x * y; A[0][2] += x * z;
      A[1][0] += y * x; A[1][1] += y * y; A[1][2] += y * z;
      A[2][0] += z * x; A[2][1] += z * y; A[2][2] += z * z;
    }

    const { values, vectors } = jacobiEigenSym3(A);
    const minIdx = argMin(values);
    const nrm = [vectors[0][minIdx], vectors[1][minIdx], vectors[2][minIdx]];
    normals[i] = normalize(nrm);
  }

  return normals;
}

function estimateAxisFromNormals(normals) {
  // Use scatter matrix S = sum(n n^T) (no mean subtraction => sign flips don't matter)
  const S = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  for (const n of normals) {
    const x = n[0], y = n[1], z = n[2];
    S[0][0] += x * x; S[0][1] += x * y; S[0][2] += x * z;
    S[1][0] += y * x; S[1][1] += y * y; S[1][2] += y * z;
    S[2][0] += z * x; S[2][1] += z * y; S[2][2] += z * z;
  }
  const { values, vectors } = jacobiEigenSym3(S);
  const minIdx = argMin(values); // direction with smallest normal energy => axis
  return [vectors[0][minIdx], vectors[1][minIdx], vectors[2][minIdx]];
}

/* ------------------------------ Circle fitting ---------------------------- */


// Algebraic LS: x^2+y^2 + A x + B y + C = 0  => solve for [A,B,C]
function fitCircleLeastSquares(pts2, inliers) {
  if (!inliers || inliers.length < 3) return null;

  let sXX = 0, sXY = 0, sX = 0;
  let sYY = 0, sY = 0;
  let s1 = 0;

  let tX = 0, tY = 0, t1 = 0;

  for (const idx of inliers) {
    const x = pts2[idx][0], y = pts2[idx][1];
    const z = x * x + y * y;

    // M^T M
    sXX += x * x;
    sXY += x * y;
    sX  += x;

    sYY += y * y;
    sY  += y;

    s1  += 1;

    // M^T (-z)
    tX += x * (-z);
    tY += y * (-z);
    t1 += 1 * (-z);
  }

  const MTM = [
    [sXX, sXY, sX],
    [sXY, sYY, sY],
    [sX,  sY,  s1],
  ];
  const MTb = [tX, tY, t1];

  const sol = solve3x3(MTM, MTb);
  if (!sol) return null;

  const A = sol[0], B = sol[1], C = sol[2];
  const cx = -A / 2;
  const cy = -B / 2;
  const r2 = (A * A + B * B) / 4 - C;
  if (!(r2 > 0)) return null;

  return { cx, cy, r: Math.sqrt(r2) };
}

/* ----------------------------- Small linear algebra ------------------------ */

// Jacobi eigen decomposition for 3x3 symmetric matrix.
// Returns eigenvalues and eigenvectors (vectors as columns of a 3x3 matrix).
function jacobiEigenSym3(Ain) {
  // Copy
  let A = [
    [Ain[0][0], Ain[0][1], Ain[0][2]],
    [Ain[1][0], Ain[1][1], Ain[1][2]],
    [Ain[2][0], Ain[2][1], Ain[2][2]],
  ];
  let V = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];

  const maxIters = 50;
  const eps = 1e-12;

  for (let iter = 0; iter < maxIters; iter++) {
    // Find largest off-diagonal element
    let p = 0, q = 1;
    let max = Math.abs(A[0][1]);

    const a02 = Math.abs(A[0][2]);
    const a12 = Math.abs(A[1][2]);
    if (a02 > max) { max = a02; p = 0; q = 2; }
    if (a12 > max) { max = a12; p = 1; q = 2; }

    if (max < eps) break;

    const app = A[p][p];
    const aqq = A[q][q];
    const apq = A[p][q];

    const phi = 0.5 * Math.atan2(2 * apq, (aqq - app));
    const c = Math.cos(phi);
    const s = Math.sin(phi);

    // Rotate A = J^T A J
    for (let k = 0; k < 3; k++) {
      const aik = A[p][k];
      const aqk = A[q][k];
      A[p][k] = c * aik - s * aqk;
      A[q][k] = s * aik + c * aqk;
    }
    for (let k = 0; k < 3; k++) {
      const aki = A[k][p];
      const akq = A[k][q];
      A[k][p] = c * aki - s * akq;
      A[k][q] = s * aki + c * akq;
    }

    // Fix exact symmetry
    A[p][q] = A[q][p];

    // Update eigenvectors V = V J
    for (let k = 0; k < 3; k++) {
      const vip = V[k][p];
      const viq = V[k][q];
      V[k][p] = c * vip - s * viq;
      V[k][q] = s * vip + c * viq;
    }
  }

  const values = [A[0][0], A[1][1], A[2][2]];
  return { values, vectors: V };
}

// Solve 3x3 linear system M x = b via Cramer's rule / adjugate (small + simple).
function solve3x3(M, b) {
  const det =
    M[0][0] * (M[1][1] * M[2][2] - M[1][2] * M[2][1]) -
    M[0][1] * (M[1][0] * M[2][2] - M[1][2] * M[2][0]) +
    M[0][2] * (M[1][0] * M[2][1] - M[1][1] * M[2][0]);

  if (Math.abs(det) < 1e-14) return null;
  const invDet = 1 / det;

  const adj = [
    [
      (M[1][1] * M[2][2] - M[1][2] * M[2][1]),
      -(M[0][1] * M[2][2] - M[0][2] * M[2][1]),
      (M[0][1] * M[1][2] - M[0][2] * M[1][1]),
    ],
    [
      -(M[1][0] * M[2][2] - M[1][2] * M[2][0]),
      (M[0][0] * M[2][2] - M[0][2] * M[2][0]),
      -(M[0][0] * M[1][2] - M[0][2] * M[1][0]),
    ],
    [
      (M[1][0] * M[2][1] - M[1][1] * M[2][0]),
      -(M[0][0] * M[2][1] - M[0][1] * M[2][0]),
      (M[0][0] * M[1][1] - M[0][1] * M[1][0]),
    ],
  ];

  // x = adj(M) * b / det
  return [
    (adj[0][0] * b[0] + adj[0][1] * b[1] + adj[0][2] * b[2]) * invDet,
    (adj[1][0] * b[0] + adj[1][1] * b[1] + adj[1][2] * b[2]) * invDet,
    (adj[2][0] * b[0] + adj[2][1] * b[1] + adj[2][2] * b[2]) * invDet,
  ];
}

/* ----------------------------- Vector helpers ----------------------------- */

function dot(a, b) { return a[0]*b[0] + a[1]*b[1] + a[2]*b[2]; }
function cross(a, b) {
  return [
    a[1]*b[2] - a[2]*b[1],
    a[2]*b[0] - a[0]*b[2],
    a[0]*b[1] - a[1]*b[0],
  ];
}
function norm(a) { return Math.hypot(a[0], a[1], a[2]); }
function normalize(a) {
  const n = norm(a);
  if (n <= 1e-15) return [0, 0, 1];
  return [a[0]/n, a[1]/n, a[2]/n];
}
function scale3(a, s) { return [a[0]*s, a[1]*s, a[2]*s]; }
function add3(a, b) { return [a[0]+b[0], a[1]+b[1], a[2]+b[2]]; }

function orthonormalBasisFromDir(d) {
  const a = Math.abs(d[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  const u = normalize(cross(d, a));
  const v = cross(d, u); // already unit if d,u unit and perpendicular
  return { u, v };
}

function argMin(arr) {
  let mi = 0;
  for (let i = 1; i < arr.length; i++) if (arr[i] < arr[mi]) mi = i;
  return mi;
}
function average(arr) {
  let s = 0;
  for (const x of arr) s += x;
  return s / Math.max(1, arr.length);
}
